import React from 'react';
import moment from 'moment';
import { defineMessages, FormattedMessage } from 'react-intl';

import { twelveHourUserSetting } from '../constants/timezone-format';

const messages = defineMessages({
  days: {
    description: 'dateService.days',
    // If we combine into one it doesn't handle 3-5 days for example.
    // eslint-disable-next-line
    defaultMessage: '{numberOfDays} {numberOfDays, plural, one {Day} other {Days}}',
    id: 'aEHuPo',
  },
});

const defaultDateFormat = 'MM/DD/YYYY';

// todo possibly change this based on internationalization
class DateServiceClass {
  dateFormat = defaultDateFormat;

  getFormattedDate(date, dateFormat = defaultDateFormat) {
    // todo make this get the user setting for real 'UI:DateFormat'
    return moment(date).format(dateFormat);
  }

  getFormattedCurrentDateTime(dateFormat = defaultDateFormat) {
    return moment().format(dateFormat);
  }

  isEmptyDate(date) {
    if (!this.isValidDate(date)) {
      return true;
    }
    const momentDate = moment(date);
    return momentDate.year() === 1;
  }

  isMaxDate(date) {
    if (!this.isValidDate(date)) {
      return false;
    }
    const momentDate = moment(date);
    return momentDate.year() === 9999;
  }

  isValidDate(date) {
    // 0 is a valid date.
    if (date === undefined || date === null) {
      return;
    }
    const momentDate = moment(date);
    return momentDate.isValid();
  }

  isValidDateString(date, format = 'MM/DD/YYYY') {
    return moment(date, format, true).isValid();
  }

  extractTime(date, format = 'h:mm a') {
    return moment.utc(date).format(format);
  }

  isSameOrBeforeToday(date) {
    return moment(date).isSameOrBefore(new Date());
  }

  /*
    Dates may come back in the following formats:
    - in number day(s) => this is the number of days
    - number => this is the number of days
    - Tommorrow by XXX
    - number-number => number of days
   */
  formatDateFromRateDate(rateDate) {
    if (!rateDate) {
      return '';
    }
    const cleanedRateDate = rateDate.replace(/(days|day|\bin\b)/g, '').trim();
    let rateDateNumber = 0;
    if (typeof cleanedRateDate === 'string') {
      rateDateNumber = Number.parseInt(cleanedRateDate, 10);
    }
    if (typeof cleanedRateDate === 'string' && cleanedRateDate.indexOf('-') !== -1) {
      return <FormattedMessage {...messages.days} values={{ numberOfDays: cleanedRateDate }} />;
    }
    if (Number.isNaN(rateDateNumber)) {
      return cleanedRateDate;
    }
    if (rateDateNumber === 0) {
      return;
    }
    return <FormattedMessage {...messages.days} values={{ numberOfDays: rateDateNumber }} />;
  }

  getDefaultDateFormat() {
    return 'MM/DD/YYYY';
  }

  getDefaultTimeFormat() {
    return 'h:mm:ss a';
  }

  getUserDateFormatSettingName() {
    return 'UI:DateFormat';
  }

  getUserTimeFormatSettingName() {
    return 'UI:TimeFormat';
  }

  isInStorageFormat(date) {
    const hasMatch = date.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
    return hasMatch && hasMatch.length > 0;
  }

  isTwelveHourFormat(format) {
    return format === twelveHourUserSetting;
  }

  isDateInstance(value) {
    return value instanceof Date;
  }

  convertToLocalDate(date) {
    /**
     * We need to convert the UTC timezone to local time i.e default runtime's timezone.
     * see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat#Syntax
     */
    return moment.utc(date).local().toDate();
  }

  getDateInFormatBackendRespects(date, userDateFormat) {
    if (!date) {
      return null;
    }
    const parsedDate = moment(date, userDateFormat);
    let formattedDate = date;
    if (typeof date !== 'string' && parsedDate.isValid()) {
      formattedDate = parsedDate.format();
    }
    const hasBeenEdited = !this.isInStorageFormat(formattedDate);
    return formattedDate && hasBeenEdited ? this.getFormattedDate(moment(formattedDate, userDateFormat)) : formattedDate;
  }

  getDateWithTimeZoneBackendRespects(date, userDateFormat, sellerTimeZone) {
    const dateBackendRespects = this.getDateInFormatBackendRespects(date, userDateFormat);
    const dateInSellerTimeZone = moment(dateBackendRespects).tz(sellerTimeZone, true).format();
    return dateInSellerTimeZone;
  }

  getAdditionalDateFormats() {
    return [
      'MM-DD-YY',
      'MM-DD-YYYY',
      'MM/DD/YY',
      'MM/DD/YYYY',
      'DD/MM/YYYY',
      'DD/MM/YY',
      'M/DD/YYYY',
      'M/DD/YY',
      'M/D/YYYY',
      'M/D/YY',
      'M-DD-YYYY',
      'M-DD-YY',
      'M-D-YYYY',
      'M-D-YY',
      'YYYY/MM/DD',
      'YY/MM/DD',
      'YYYY-MM-DD',
      'YY-MM-DD',
      'YYYY/M/DD',
      'YY/M/DD',
      'YYYY-M-DD',
      'YY-M-DD',
      'YYYY/MM/D',
      'YYYY/M/D',
      'YY/MM/D',
      'YY/M/D',
      'MMDDYY',
      'MMDDYYYY',
      'YYYYMMDD',
    ];
  }
}

export const DateService = new DateServiceClass();
